import React, { useEffect, useState } from 'react';
import { collection, getDocs, doc, updateDoc, query, where } from 'firebase/firestore';
import { db } from '../config/firebase';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import {
  Search,
  CheckCircle,
  XCircle,
  Clock,
  Receipt,
  X,
  ExternalLink,
  FileText,
  CalendarDays,
  User as UserIcon,
} from 'lucide-react';
import toast from 'react-hot-toast';
import Pagination from '../components/Pagination';

/**
 * Payment Verifications
 *
 * The bank-transfer payment method is manual: the guest uploads a transfer
 * receipt and the booking moves to `pending`. This page is the admin gate that
 * sits BEFORE the host ever sees the request:
 *
 *   pending  ── admin verifies receipt ──▶  adminApproved  ──▶  host accepts/rejects
 *   pending  ── admin declines receipt ──▶  paymentRejected (guest notified, nothing else)
 *
 * Only bank-transfer payments need manual review here; gateway (card) payments
 * are confirmed automatically by the Moyasar webhook and never reach this queue.
 */
const PaymentVerifications = () => {
  const { colors } = useTheme();
  const { currentUser } = useAuth();
  const [rows, setRows] = useState([]);
  const [filteredRows, setFilteredRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('pending');
  const [selected, setSelected] = useState(null);
  const [declineReason, setDeclineReason] = useState('');
  const [showDeclineModal, setShowDeclineModal] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 6;

  useEffect(() => {
    fetchPending();
  }, []);

  useEffect(() => {
    filterRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, searchTerm, filterStatus]);

  // Build the review queue: bank-transfer bookings awaiting verification, plus
  // recently processed ones so admins can audit their own decisions.
  const fetchPending = async () => {
    setLoading(true);
    try {
      // Bookings in the relevant states. We over-fetch a little (the three states
      // this page manages) and attach the payment record for each.
      const reviewStates = ['pending', 'adminApproved', 'paymentRejected'];
      const bookingsSnap = await getDocs(
        query(collection(db, 'bookings'), where('status', 'in', reviewStates)),
      );

      const bookings = bookingsSnap.docs.map((d) => ({ id: d.id, ...d.data() }));

      // Attach the bank-transfer payment + property/guest display info.
      const enriched = await Promise.all(
        bookings.map(async (b) => {
          let payment = null;
          try {
            const paySnap = await getDocs(
              query(collection(db, 'payments'), where('bookingId', '==', b.id)),
            );
            // Prefer a bank-transfer record; fall back to any payment record.
            const payments = paySnap.docs.map((p) => ({ id: p.id, ...p.data() }));
            payment =
              payments.find((p) => p.paymentMethod === 'bank_transfer') ||
              payments[0] ||
              null;
          } catch (err) {
            console.warn('Could not load payment for booking', b.id, err);
          }

          let propertyTitle = b.propertyTitle || 'Property';
          try {
            if (b.propertyId) {
              const propSnap = await getDocs(
                query(collection(db, 'properties'), where('__name__', '==', b.propertyId)),
              );
              if (!propSnap.empty) {
                propertyTitle = propSnap.docs[0].data().title || propertyTitle;
              }
            }
          } catch (err) {
            // non-fatal
          }

          return { ...b, payment, propertyTitle };
        }),
      );

      // Only show bank-transfer flows here; gateway payments are auto-confirmed.
      const bankOnly = enriched.filter(
        (r) => r.payment?.paymentMethod === 'bank_transfer',
      );

      // Newest first by payment/booking creation time.
      bankOnly.sort((a, b) => {
        const ta = a.payment?.createdAt?.toMillis?.() ?? a.createdAt?.toMillis?.() ?? 0;
        const tb = b.payment?.createdAt?.toMillis?.() ?? b.createdAt?.toMillis?.() ?? 0;
        return tb - ta;
      });

      setRows(bankOnly);
    } catch (error) {
      console.error('Error fetching payment verifications:', error);
      toast.error('Failed to load payment verifications');
    } finally {
      setLoading(false);
    }
  };

  const filterRows = () => {
    let filtered = rows;

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (r) =>
          r.propertyTitle?.toLowerCase().includes(term) ||
          r.id?.toLowerCase().includes(term) ||
          r.guestName?.toLowerCase().includes(term),
      );
    }

    if (filterStatus !== 'all') {
      filtered = filtered.filter((r) => (r.status || 'pending') === filterStatus);
    }

    setFilteredRows(filtered);
    setCurrentPage(1);
  };

  // Approve the receipt → release the booking to the host.
  const verifyPayment = async (row) => {
    if (!row?.payment) {
      toast.error('No payment record attached to this booking.');
      return;
    }
    setActionLoading(true);
    try {
      // Mark the payment as confirmed by the admin (provenance for earnings).
      await updateDoc(doc(db, 'payments', row.payment.id), {
        status: 'paid',
        verifiedBy: currentUser?.uid || 'admin',
        verifiedAt: new Date(),
        updatedAt: new Date(),
      });

      // Advance the booking to adminApproved → the host gets the request.
      await updateDoc(doc(db, 'bookings', row.id), {
        status: 'adminApproved',
        paymentStatus: 'paid',
        paymentVerifiedBy: currentUser?.uid || 'admin',
        paymentVerifiedAt: new Date(),
        updatedAt: new Date(),
      });

      setRows((prev) =>
        prev.map((r) =>
          r.id === row.id
            ? { ...r, status: 'adminApproved', payment: { ...r.payment, status: 'paid' } }
            : r,
        ),
      );
      toast.success('Payment verified — sent to host for approval');
      setSelected(null);
    } catch (error) {
      console.error('Error verifying payment:', error);
      toast.error('Failed to verify payment');
    } finally {
      setActionLoading(false);
    }
  };

  // Decline the receipt → notify the guest, no money moves, host never sees it.
  const declinePayment = async () => {
    if (!selected) return;
    setActionLoading(true);
    try {
      if (selected.payment) {
        await updateDoc(doc(db, 'payments', selected.payment.id), {
          status: 'failed',
          declineReason: declineReason || 'Payment proof could not be verified',
          verifiedBy: currentUser?.uid || 'admin',
          verifiedAt: new Date(),
          updatedAt: new Date(),
        });
      }

      await updateDoc(doc(db, 'bookings', selected.id), {
        status: 'paymentRejected',
        paymentStatus: 'failed',
        paymentDeclineReason: declineReason || 'Payment proof could not be verified',
        paymentVerifiedBy: currentUser?.uid || 'admin',
        paymentVerifiedAt: new Date(),
        updatedAt: new Date(),
      });

      setRows((prev) =>
        prev.map((r) =>
          r.id === selected.id
            ? {
                ...r,
                status: 'paymentRejected',
                paymentDeclineReason: declineReason,
                payment: r.payment ? { ...r.payment, status: 'failed' } : null,
              }
            : r,
        ),
      );
      toast.success('Payment declined — guest has been notified');
      setShowDeclineModal(false);
      setDeclineReason('');
      setSelected(null);
    } catch (error) {
      console.error('Error declining payment:', error);
      toast.error('Failed to decline payment');
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { bg: colors.warningGhost, color: colors.warning, icon: Clock, text: 'Awaiting Review' },
      adminApproved: { bg: colors.successGhost, color: colors.success, icon: CheckCircle, text: 'Verified' },
      paymentRejected: { bg: colors.errorGhost, color: colors.error, icon: XCircle, text: 'Declined' },
    };
    const badge = badges[status] || badges.pending;
    const Icon = badge.icon;
    return (
      <div
        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold tracking-wide uppercase shadow-sm"
        style={{ backgroundColor: badge.bg, color: badge.color, border: `1px solid ${badge.color}30`, backdropFilter: 'blur(8px)' }}
      >
        <Icon className="w-3.5 h-3.5" />
        {badge.text}
      </div>
    );
  };

  const formatMoney = (value) => `${Number(value || 0).toLocaleString()} SAR`;
  const formatDate = (ts) =>
    ts?.toDate?.()?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) || 'Unknown';

  const stats = {
    pending: rows.filter((r) => (r.status || 'pending') === 'pending').length,
    adminApproved: rows.filter((r) => r.status === 'adminApproved').length,
    paymentRejected: rows.filter((r) => r.status === 'paymentRejected').length,
  };

  const statCards = [
    { id: 'pending', label: 'Awaiting Review', value: stats.pending, icon: Clock, color: colors.warning, gradient: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)' },
    { id: 'adminApproved', label: 'Verified', value: stats.adminApproved, icon: CheckCircle, color: colors.success, gradient: 'linear-gradient(135deg, #22C55E 0%, #16A34A 100%)' },
    { id: 'paymentRejected', label: 'Declined', value: stats.paymentRejected, icon: XCircle, color: colors.error, gradient: 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)' },
  ];

  if (loading) {
    return (
      <div className="space-y-6">
        <div>
          <div className="skeleton h-8 w-48 mb-2" style={{ backgroundColor: colors.border }} />
          <div className="skeleton h-4 w-72" style={{ backgroundColor: colors.border }} />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="rounded-2xl p-4 h-[100px]" style={{ backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}>
              <div className="skeleton h-4 w-24 mb-4" style={{ backgroundColor: colors.border }} />
              <div className="skeleton h-7 w-12" style={{ backgroundColor: colors.border }} />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight" style={{ color: colors.text }}>
            Payment Verifications
          </h1>
          <p className="mt-1 text-sm" style={{ color: colors.textSecondary }}>
            Review bank-transfer receipts. Verified payments are released to the host; declined ones notify the guest.
          </p>
        </div>
      </div>

      {/* Stat filters */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {statCards.map((stat, i) => {
          const isActive = filterStatus === stat.id;
          return (
            <div
              key={stat.id}
              className={`card-premium p-4 cursor-pointer transition-all ${isActive ? 'ring-2' : ''} opacity-0 animate-fadeInScale stagger-${i + 1}`}
              style={{
                backgroundColor: isActive ? `${stat.color}10` : colors.card,
                border: `1px solid ${isActive ? stat.color : colors.border}`,
                boxShadow: isActive ? `0 4px 20px ${stat.color}20` : undefined,
                ringColor: isActive ? stat.color : 'transparent',
              }}
              onClick={() => setFilterStatus(stat.id)}
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[12px] font-semibold uppercase tracking-wider mb-1" style={{ color: isActive ? stat.color : colors.textSecondary }}>{stat.label}</p>
                  <p className="text-2xl font-bold" style={{ color: colors.text }}>{stat.value}</p>
                </div>
                <div className="w-10 h-10 rounded-xl flex items-center justify-center shadow-sm" style={{ background: stat.gradient }}>
                  <stat.icon className="w-5 h-5 text-white" />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Search */}
      <div className="card-premium p-4" style={{ backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}>
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3.5 top-1/2 transform -translate-y-1/2 w-[18px] h-[18px]" style={{ color: colors.textMuted }} />
            <input
              type="text"
              placeholder="Search by property, guest, booking ID..."
              className="w-full pl-11 pr-4 py-2.5 rounded-xl text-[14px] focus:outline-none transition-all duration-200"
              style={{ backgroundColor: colors.backgroundSecondary, border: `1px solid ${colors.border}`, color: colors.text }}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <button
            onClick={() => setFilterStatus('all')}
            className="px-5 py-2.5 rounded-xl text-[13px] font-semibold transition-all hover:shadow-md"
            style={{
              backgroundColor: filterStatus === 'all' ? colors.primary : colors.backgroundSecondary,
              color: filterStatus === 'all' ? '#FFFFFF' : colors.text,
              border: `1px solid ${filterStatus === 'all' ? colors.primaryDark : colors.border}`,
            }}
          >
            Show All
          </button>
        </div>
      </div>

      {/* List */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5 lg:gap-6">
        {filteredRows.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage).map((row, idx) => (
          <div
            key={row.id}
            className={`card-premium overflow-hidden transition-all cursor-pointer p-5 opacity-0 animate-fadeInScale stagger-${Math.min(idx + 1, 6)}`}
            style={{ backgroundColor: colors.card, border: `1px solid ${colors.border}` }}
            onClick={() => setSelected(row)}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-center gap-2.5 min-w-0">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: colors.primaryGhost }}>
                  <Receipt className="w-5 h-5" style={{ color: colors.primary }} />
                </div>
                <div className="min-w-0">
                  <h3 className="text-[15px] font-bold truncate" style={{ color: colors.text }}>{row.propertyTitle}</h3>
                  <p className="text-[12px] truncate" style={{ color: colors.textMuted }}>#{row.id.slice(0, 10)}</p>
                </div>
              </div>
              {getStatusBadge(row.status || 'pending')}
            </div>

            <div className="mt-4 grid grid-cols-2 gap-3">
              <div>
                <p className="text-[11px] uppercase tracking-wider font-semibold mb-0.5" style={{ color: colors.textMuted }}>Amount</p>
                <p className="text-[15px] font-bold" style={{ color: colors.primary }}>{formatMoney(row.payment?.amount ?? row.totalAmount)}</p>
              </div>
              <div>
                <p className="text-[11px] uppercase tracking-wider font-semibold mb-0.5" style={{ color: colors.textMuted }}>Submitted</p>
                <p className="text-[13px] font-medium" style={{ color: colors.text }}>{formatDate(row.payment?.createdAt ?? row.createdAt)}</p>
              </div>
            </div>

            <div className="mt-4 pt-3 flex items-center gap-2" style={{ borderTop: `1px dashed ${colors.border}` }}>
              <FileText className="w-3.5 h-3.5" style={{ color: colors.textMuted }} />
              <span className="text-[12px] font-medium" style={{ color: colors.textSecondary }}>
                {row.payment?.metadata?.receiptUrl ? 'Receipt attached' : 'No receipt'}
              </span>
            </div>
          </div>
        ))}
      </div>

      {filteredRows.length === 0 && (
        <div className="flex flex-col items-center justify-center p-12 card-premium" style={{ backgroundColor: colors.card, border: `1px solid ${colors.border}` }}>
          <div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-4" style={{ backgroundColor: colors.backgroundSecondary }}>
            <Receipt className="w-8 h-8" style={{ color: colors.textMuted }} />
          </div>
          <h3 className="text-[15px] font-semibold" style={{ color: colors.text }}>No payments to review</h3>
          <p className="mt-1 text-[13px]" style={{ color: colors.textSecondary }}>
            {filterStatus === 'pending' ? "You're all caught up! No bank transfers awaiting review." : 'Try adjusting your search or filters.'}
          </p>
        </div>
      )}

      {filteredRows.length > 0 && (
        <Pagination currentPage={currentPage} totalItems={filteredRows.length} itemsPerPage={itemsPerPage} onPageChange={setCurrentPage} />
      )}

      {/* Detail modal */}
      {selected && (
        <div className="admin-modal-overlay fixed inset-0 z-[60] flex items-center justify-center p-4 sm:p-6 lg:p-8 animate-fadeIn" style={{ backgroundColor: 'rgba(11, 17, 32, 0.75)', backdropFilter: 'blur(8px)' }}>
          <div className="admin-modal-card admin-modal-scroll rounded-2xl max-w-2xl w-full shadow-2xl relative animate-fadeInScale flex flex-col" style={{ backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}>
            <div className="flex items-center justify-between p-6 border-b" style={{ borderColor: colors.border }}>
              <div>
                <h2 className="text-xl font-bold" style={{ color: colors.text }}>{selected.propertyTitle}</h2>
                <p className="text-[12px] mt-0.5" style={{ color: colors.textMuted }}>Booking #{selected.id}</p>
              </div>
              <button onClick={() => setSelected(null)} className="p-2 rounded-full transition-all hover:bg-white/10" style={{ color: colors.textMuted }}>
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 sm:p-8 space-y-6">
              <div className="flex items-center justify-between">
                {getStatusBadge(selected.status || 'pending')}
                <span className="text-[12px] font-medium px-2.5 py-1 rounded-md" style={{ backgroundColor: colors.backgroundSecondary, color: colors.textSecondary }}>
                  Bank transfer
                </span>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-5 gap-x-4">
                <div>
                  <p className="text-[11px] uppercase tracking-wider font-semibold mb-1" style={{ color: colors.textMuted }}>Amount paid</p>
                  <p className="text-[18px] font-bold" style={{ color: colors.primary }}>{formatMoney(selected.payment?.amount ?? selected.totalAmount)}</p>
                </div>
                <div>
                  <p className="text-[11px] uppercase tracking-wider font-semibold mb-1" style={{ color: colors.textMuted }}>Submitted</p>
                  <p className="text-[14px] font-medium flex items-center gap-1.5" style={{ color: colors.text }}>
                    <CalendarDays className="w-3.5 h-3.5" style={{ color: colors.textMuted }} />
                    {formatDate(selected.payment?.createdAt ?? selected.createdAt)}
                  </p>
                </div>
                <div>
                  <p className="text-[11px] uppercase tracking-wider font-semibold mb-1" style={{ color: colors.textMuted }}>Stay</p>
                  <p className="text-[14px] font-medium" style={{ color: colors.text }}>
                    {formatDate(selected.startDate)} → {formatDate(selected.endDate)}
                  </p>
                </div>
                <div>
                  <p className="text-[11px] uppercase tracking-wider font-semibold mb-1" style={{ color: colors.textMuted }}>Occupants</p>
                  <p className="text-[14px] font-medium flex items-center gap-1.5" style={{ color: colors.text }}>
                    <UserIcon className="w-3.5 h-3.5" style={{ color: colors.textMuted }} />
                    {selected.occupantCount || 1}
                  </p>
                </div>
              </div>

              {/* Receipt */}
              <div>
                <p className="text-[13px] font-semibold mb-3 uppercase tracking-wider" style={{ color: colors.textSecondary }}>Transfer receipt</p>
                {selected.payment?.metadata?.receiptUrl ? (
                  <a
                    href={selected.payment.metadata.receiptUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-between p-4 rounded-xl transition-all hover:shadow-md"
                    style={{ backgroundColor: colors.backgroundSecondary, border: `1px solid ${colors.border}`, color: colors.text }}
                  >
                    <span className="flex items-center gap-2.5 text-[14px] font-medium">
                      <FileText className="w-5 h-5" style={{ color: colors.primary }} />
                      {selected.payment.metadata.receiptFileName || 'View receipt'}
                    </span>
                    <ExternalLink className="w-4 h-4" style={{ color: colors.textMuted }} />
                  </a>
                ) : (
                  <div className="p-4 rounded-xl text-[14px]" style={{ backgroundColor: colors.errorGhost, color: colors.error, border: `1px solid ${colors.error}30` }}>
                    No receipt was attached to this payment.
                  </div>
                )}
              </div>

              {selected.paymentDeclineReason && (
                <div className="p-4 rounded-xl flex gap-3" style={{ backgroundColor: colors.errorGhost, border: `1px solid ${colors.error}40` }}>
                  <XCircle className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: colors.error }} />
                  <div>
                    <p className="text-[13px] font-bold" style={{ color: colors.error }}>Decline reason</p>
                    <p className="text-[14px] mt-1 font-medium" style={{ color: colors.text }}>{selected.paymentDeclineReason}</p>
                  </div>
                </div>
              )}
            </div>

            {/* Action bar */}
            {(selected.status || 'pending') === 'pending' && (
              <div className="sticky bottom-0 p-6 sm:p-8 border-t" style={{ borderColor: colors.border, backgroundColor: colors.surface }}>
                <div className="flex flex-col sm:flex-row gap-3">
                  <button
                    onClick={() => verifyPayment(selected)}
                    disabled={actionLoading}
                    className="btn-primary flex-1 py-3 rounded-xl font-bold text-white shadow-lg flex justify-center items-center gap-2 disabled:opacity-50"
                    style={{ background: 'linear-gradient(135deg, #22C55E 0%, #16A34A 100%)' }}
                  >
                    <CheckCircle className="w-[18px] h-[18px]" /> Verify & Send to Host
                  </button>
                  <button
                    onClick={() => setShowDeclineModal(true)}
                    disabled={actionLoading}
                    className="flex-1 py-3 rounded-xl font-bold text-white shadow-lg flex justify-center items-center gap-2 transition-transform hover:-translate-y-0.5 disabled:opacity-50"
                    style={{ background: 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)' }}
                  >
                    <XCircle className="w-[18px] h-[18px]" /> Decline Payment
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Decline modal */}
      {showDeclineModal && (
        <div className="fixed inset-0 flex items-center justify-center z-[70] p-4 animate-fadeIn" style={{ backgroundColor: 'rgba(11, 17, 32, 0.8)', backdropFilter: 'blur(8px)' }}>
          <div className="rounded-2xl max-w-md w-full p-6 sm:p-8 animate-fadeInScale shadow-2xl" style={{ backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}>
            <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4" style={{ backgroundColor: colors.errorGhost }}>
              <XCircle className="w-6 h-6" style={{ color: colors.error }} />
            </div>
            <h3 className="text-xl font-bold" style={{ color: colors.text }}>Decline Payment</h3>
            <p className="mt-2 text-[14px] leading-relaxed" style={{ color: colors.textSecondary }}>
              The guest will be notified that their payment could not be verified. No funds move and the host will not see this booking.
            </p>
            <textarea
              className="w-full mt-5 p-3.5 rounded-xl text-[14px] focus:outline-none transition-all"
              style={{ backgroundColor: colors.backgroundSecondary, border: `1px solid ${colors.border}`, color: colors.text }}
              rows={4}
              placeholder="e.g. Receipt amount does not match, transfer not received, image unreadable..."
              value={declineReason}
              onChange={(e) => setDeclineReason(e.target.value)}
            />
            <div className="flex gap-3 mt-8">
              <button
                onClick={() => { setShowDeclineModal(false); setDeclineReason(''); }}
                className="flex-[1] py-2.5 rounded-xl font-semibold transition-all hover:bg-opacity-80"
                style={{ backgroundColor: colors.background, color: colors.text, border: `1px solid ${colors.border}` }}
              >
                Cancel
              </button>
              <button
                onClick={declinePayment}
                disabled={!declineReason.trim() || actionLoading}
                className="flex-[2] py-2.5 rounded-xl font-bold text-white transition-all hover:opacity-90 disabled:opacity-50"
                style={{ background: 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)', boxShadow: '0 4px 14px rgba(239, 68, 68, 0.3)' }}
              >
                Confirm Decline
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentVerifications;
